export interface FileMetadata {
  name: string;
  size: number;
  type: string;
  lastModified: number;
  extension: string;
}

export interface FileValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  metadata: FileMetadata;
}

// Supported document types
export const SUPPORTED_DOCUMENT_TYPES = {
  PDF: ['.pdf'],
  IMAGE: ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.webp'],
  EXCEL: ['.xlsx', '.xls'],
  WORD: ['.docx', '.doc'],
  TEXT: ['.txt', '.csv'],
} as const;

export const ALL_SUPPORTED_EXTENSIONS = Object.values(SUPPORTED_DOCUMENT_TYPES).flat();

// File size limits (in bytes)
export const FILE_SIZE_LIMITS = {
  PDF: 50 * 1024 * 1024, // 50MB
  IMAGE: 10 * 1024 * 1024, // 10MB
  EXCEL: 25 * 1024 * 1024, // 25MB
  WORD: 25 * 1024 * 1024, // 25MB
  TEXT: 5 * 1024 * 1024, // 5MB
} as const;

// Extract file metadata
export const getFileMetadata = (file: File): FileMetadata => {
  const extension = '.' + file.name.split('.').pop()?.toLowerCase() || '';
  
  return {
    name: file.name,
    size: file.size,
    type: file.type,
    lastModified: file.lastModified,
    extension,
  };
};

// Validate a single file
export const validateFile = (file: File): FileValidationResult => {
  const metadata = getFileMetadata(file);
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check if file extension is supported
  if (!ALL_SUPPORTED_EXTENSIONS.includes(metadata.extension as any)) {
    errors.push(`File type ${metadata.extension} is not supported`);
  }

  // Check file size limits
  const documentType = getDocumentType(metadata.extension);
  if (documentType) {
    const sizeLimit = FILE_SIZE_LIMITS[documentType];
    if (metadata.size > sizeLimit) {
      errors.push(`File size exceeds ${formatFileSize(sizeLimit)} limit for ${documentType} files`);
    }
  }

  // Check for empty files
  if (metadata.size === 0) {
    errors.push('File is empty');
  }

  // Check for very large file names
  if (metadata.name.length > 255) {
    errors.push('File name is too long (max 255 characters)');
  }

  // Check for potentially problematic characters in filename
  const problematicChars = /[<>:"|?*]/;
  if (problematicChars.test(metadata.name)) {
    warnings.push('File name contains special characters that may cause issues');
  }

  // Warn about very large files
  if (metadata.size > 100 * 1024 * 1024) { // 100MB
    warnings.push('Large file may take longer to process');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    metadata,
  };
};

// Validate multiple files
export const validateFiles = (files: File[]): { 
  valid: File[]; 
  invalid: Array<{ file: File; result: FileValidationResult }>;
  totalSize: number;
} => {
  const valid: File[] = [];
  const invalid: Array<{ file: File; result: FileValidationResult }> = [];
  let totalSize = 0;

  files.forEach(file => {
    const result = validateFile(file);
    totalSize += file.size;
    
    if (result.isValid) {
      valid.push(file);
    } else {
      invalid.push({ file, result });
    }
  });

  return { valid, invalid, totalSize };
};

// Get document type from file extension
export const getDocumentType = (extension: string): keyof typeof SUPPORTED_DOCUMENT_TYPES | null => {
  for (const [type, extensions] of Object.entries(SUPPORTED_DOCUMENT_TYPES)) {
    if ((extensions as readonly string[]).includes(extension)) {
      return type as keyof typeof SUPPORTED_DOCUMENT_TYPES;
    }
  }
  return null;
};

// Format file size for display
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Convert file to base64
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        // Remove the data:mime/type;base64, prefix
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      } else {
        reject(new Error('Failed to convert file to base64'));
      }
    };
    reader.onerror = (error) => reject(error);
  });
};

// Convert base64 to blob
export const base64ToBlob = (base64: string, contentType = ''): Blob => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: contentType });
};

// Create download link for file
export const downloadFile = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Get MIME type from file extension
export const getMimeType = (extension: string): string => {
  const mimeTypes: Record<string, string> = {
    '.pdf': 'application/pdf',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.gif': 'image/gif',
    '.bmp': 'image/bmp',
    '.tiff': 'image/tiff',
    '.webp': 'image/webp',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.xls': 'application/vnd.ms-excel',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.doc': 'application/msword',
    '.txt': 'text/plain',
    '.csv': 'text/csv',
  };
  
  return mimeTypes[extension.toLowerCase()] || 'application/octet-stream';
};

// Extract document type from business context
export const inferDocumentType = (filename: string): string => {
  const name = filename.toLowerCase();
  
  if (name.includes('invoice') || name.includes('inv')) {
    return 'commercialInvoice';
  }
  if (name.includes('packing') || name.includes('pack')) {
    return 'packingList';
  }
  if (name.includes('bol') || name.includes('bill') || name.includes('lading')) {
    return 'billOfLading';
  }
  if (name.includes('awb') || name.includes('airway')) {
    return 'airWaybill';
  }
  if (name.includes('origin') || name.includes('certificate')) {
    return 'certificateOfOrigin';
  }
  if (name.includes('sds') || name.includes('safety') || name.includes('msds')) {
    return 'safetyDataSheet';
  }
  if (name.includes('dangerous') || name.includes('dg') || name.includes('hazmat')) {
    return 'dangerousGoodsDeclaration';
  }
  
  // Default fallback
  return 'commercialInvoice';
};

// Check if file is likely an image
export const isImageFile = (file: File): boolean => {
  return (SUPPORTED_DOCUMENT_TYPES.IMAGE as readonly string[]).includes(getFileMetadata(file).extension);
};

// Check if file is likely a PDF
export const isPdfFile = (file: File): boolean => {
  return (SUPPORTED_DOCUMENT_TYPES.PDF as readonly string[]).includes(getFileMetadata(file).extension);
};

// Batch process files
export const processBatch = async <T>(
  files: File[],
  processor: (file: File) => Promise<T>,
  options: {
    batchSize?: number;
    onProgress?: (completed: number, total: number) => void;
    onError?: (file: File, error: Error) => void;
  } = {}
): Promise<Array<{ file: File; result?: T; error?: Error }>> => {
  const { batchSize = 3, onProgress, onError } = options;
  const results: Array<{ file: File; result?: T; error?: Error }> = [];
  
  for (let i = 0; i < files.length; i += batchSize) {
    const batch = files.slice(i, i + batchSize);
    
    const batchPromises = batch.map(async (file) => {
      try {
        const result = await processor(file);
        return { file, result };
      } catch (error) {
        const err = error instanceof Error ? error : new Error('Unknown error');
        if (onError) {
          onError(file, err);
        }
        return { file, error: err };
      }
    });
    
    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);
    
    if (onProgress) {
      onProgress(results.length, files.length);
    }
  }
  
  return results;
};